import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import Card from "./Card";
import { POST_PLATFORMS } from "../lib/helpers";
import { useConfirm } from "./ConfirmDialog";
import { toast } from "sonner";
import * as Icons from "lucide-react";

// Les listes de comptes à identifier d'UNE entreprise (décision de Mélilia du 27/10).
//
// Elle les compose et les nomme elle-même : « Implantologie » peut mélanger Straumann et ITI, et
// n'a de sens que pour ce client-là. Les comptes viennent de l'ANNUAIRE, qui les range par centre
// d'intérêt ; un compte saisi ici y remonte aussitôt, marqué NON VÉRIFIÉ — il sert tout de suite,
// et l'annuaire dit qu'on ne l'a pas encore confirmé.
//
// L'entreprise voit ses listes. Jamais l'annuaire entier.

const Pastille = ({ c, onRetirer }) => (
  // `max-w-full` + pseudo tronqué : une pastille ne doit jamais dépasser de sa carte à 320 px
  // (mesuré jusqu'à 47 px sur la page Entités). Le réseau, « à vérifier » et la croix gardent
  // leur place : c'est le pseudo, la partie longue, qui se tronque.
  <span className={`inline-flex max-w-full items-center gap-1.5 px-2 py-0.5 rounded-full text-xs border ${
    c.blocked ? "bg-rouge-50 border-rouge/40 text-rouge-texte line-through" : "bg-sable border-marine/20 text-marine"}`}
    title={c.blocked ? `Ne rien taguer — ${c.blocked_reason || "sans raison notée"}` : `${c.entity_name} · ${c.verified ? "vérifié" : "à vérifier"}`}>
    <span className="truncate min-w-0">{c.handle}</span>
    <span className="text-marine/80 shrink-0">{c.network}</span>
    {!c.verified && !c.blocked && <span title="À vérifier" className="shrink-0 px-1.5 rounded-sm bg-ambre-50 text-ambre-texte border border-ambre/40">à vérifier</span>}
    {onRetirer && <button aria-label={`Retirer ${c.handle} de la liste`} onClick={onRetirer} className="shrink-0 text-rouge-texte"><Icons.X size={16} /></button>}
  </span>
);

export default function TagLists({ clientId }) {
  const { confirm, ask } = useConfirm();
  const [listes, setListes] = useState([]);
  const [annuaire, setAnnuaire] = useState([]);
  const [ouvert, setOuvert] = useState(null);
  const [choix, setChoix] = useState("");
  const [neuf, setNeuf] = useState({ network: "Instagram", handle: "", name: "", entity_id: "", entity_name: "" });

  const charger = useCallback(async () => {
    const [l, e] = await Promise.all([
      api.get(`/admin/clients/${clientId}/tag-lists`),
      api.get("/admin/entities"),
    ]);
    setListes(l.data); setAnnuaire(e.data);
  }, [clientId]);
  useEffect(() => { charger().catch(() => toast.error("Chargement impossible")); }, [charger]);

  // L'annuaire rangé par centre d'intérêt, pour le sélecteur.
  const parInteret = useMemo(() => annuaire
    .filter((e) => (e.accounts || []).length)
    .map((e) => ({ nom: e.name, comptes: e.accounts })), [annuaire]);

  const erreur = (e, defaut) => toast.error(e?.response?.data?.detail || defaut);

  const creer = async () => {
    const nom = await ask({ title: "Nouvelle liste", label: "Son nom", confirmLabel: "Créer" });
    if (!nom?.trim()) return;
    try { await api.post(`/admin/clients/${clientId}/tag-lists`, { name: nom.trim() }); charger(); toast.success("Liste créée"); }
    catch (e) { erreur(e, "Création impossible"); }
  };
  const renommer = async (l) => {
    const nom = await ask({ title: "Renommer la liste", label: "Son nom", defaultValue: l.name, confirmLabel: "Renommer" });
    if (!nom?.trim() || nom.trim() === l.name) return;
    try { await api.put(`/admin/tag-lists/${l.id}`, { name: nom.trim() }); charger(); toast.success("Liste renommée"); }
    catch (e) { erreur(e, "Renommage impossible"); }
  };
  const supprimer = async (l) => {
    if (!(await confirm({ title: `Supprimer la liste « ${l.name} » ?`, message: "Les comptes restent dans l'annuaire ; seule la liste disparaît.", tone: "danger" }))) return;
    try { await api.delete(`/admin/tag-lists/${l.id}`); charger(); toast.success("Liste supprimée"); }
    catch (e) { erreur(e, "Suppression impossible"); }
  };
  const ids = async (l, suite, message) => {
    try { await api.put(`/admin/tag-lists/${l.id}`, { account_ids: suite }); await charger(); if (message) toast.success(message); }
    catch (e) { erreur(e, "Enregistrement impossible"); }
  };
  const depuisAnnuaire = async (l) => {
    if (!choix) return toast.error("Choisissez un compte dans l'annuaire");
    const compte = annuaire.flatMap((e) => e.accounts || []).find((c) => c.id === choix);
    await ids(l, [...(l.account_ids || []), choix], `${compte ? compte.handle : "Compte"} ajouté à « ${l.name} »`);
    setChoix("");
  };
  const ajouterNeuf = async (l) => {
    if (!neuf.handle.trim()) return toast.error("Indiquez l'identifiant du compte");
    if (!neuf.entity_id && !neuf.entity_name.trim()) return toast.error("Indiquez un centre d'intérêt, existant ou nouveau");
    try {
      await api.post(`/admin/tag-lists/${l.id}/accounts`, {
        network: neuf.network, handle: neuf.handle.trim(), name: neuf.name.trim(),
        entity_id: neuf.entity_id || null, entity_name: neuf.entity_id ? null : neuf.entity_name.trim() });
      setNeuf({ network: "Instagram", handle: "", name: "", entity_id: "", entity_name: "" });
      charger(); toast.success("Compte ajouté ici et à l'annuaire, à vérifier");
    } catch (e) { erreur(e, "Ajout impossible"); }
  };

  return (
    <Card title="Listes de comptes à identifier"
      action={<button onClick={creer} data-testid="liste-nouvelle"
        className="text-sm border border-marine text-marine rounded-sm px-3 py-1.5 hover:bg-marine hover:text-white transition-colors flex items-center gap-1">
        <Icons.Plus size={16} /> Nouvelle liste</button>}>
      <p className="text-sm text-marine/80 mb-4">
        Vos groupes pour cette entreprise, que vous nommez vous-même. Les comptes viennent de l'annuaire ;
        un compte saisi ici y remonte aussitôt, marqué <b>à vérifier</b>. <b>L'entreprise voit ses listes</b> et
        peut en demander la modification — jamais l'annuaire entier.
      </p>

      <div className="space-y-3" data-testid="listes-tags">
        {listes.length === 0 && <p className="text-sm text-marine/80">Aucune liste pour cette entreprise. Créez-en une : « Implantologie », « Laboratoires »…</p>}
        {listes.map((l) => {
          const deplie = ouvert === l.id;
          return (
            <div key={l.id} data-testid={`liste-${l.id}`} className="rounded-sm border border-marine/10 p-3">
              <div className="flex flex-wrap items-center gap-3">
                <button onClick={() => setOuvert(deplie ? null : l.id)} data-testid={`liste-ouvrir-${l.id}`}
                  className="font-medium text-marine flex items-center gap-1.5 hover:opacity-70">
                  <Icons.ChevronRight size={16} className={`text-marine transition-transform ${deplie ? "rotate-90" : ""}`} />
                  {l.name}
                </button>
                <span className="text-xs text-marine/80">{(l.accounts || []).length} compte{(l.accounts || []).length > 1 ? "s" : ""}</span>
                <div className="ml-auto flex items-center gap-2 shrink-0">
                  <button aria-label={`Renommer ${l.name}`} onClick={() => renommer(l)} data-testid={`liste-renommer-${l.id}`} className="text-marine hover:opacity-70"><Icons.Pencil size={16} /></button>
                  <button aria-label={`Supprimer ${l.name}`} onClick={() => supprimer(l)} data-testid={`liste-supprimer-${l.id}`} className="text-rouge-texte hover:opacity-70"><Icons.Trash2 size={16} /></button>
                </div>
              </div>

              {(l.accounts || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2 pl-6">
                  {l.accounts.map((c) => (
                    <Pastille key={c.id} c={c}
                      onRetirer={deplie ? () => ids(l, (l.account_ids || []).filter((x) => x !== c.id), `${c.handle} retiré de « ${l.name} »`) : null} />
                  ))}
                </div>
              )}

              {deplie && (
                <div className="mt-3 pl-6 pt-3 border-t border-marine/10 space-y-4">
                  <div className="flex flex-wrap items-end gap-2">
                    <label className="text-sm flex-1 min-w-[260px]">Ajouter depuis l'annuaire
                      <select value={choix} onChange={(e) => setChoix(e.target.value)} data-testid={`liste-choix-${l.id}`} className="inp mt-1">
                        <option value="">— Choisir un compte —</option>
                        {parInteret.map((g) => (
                          <optgroup key={g.nom} label={g.nom}>
                            {g.comptes.filter((c) => !(l.account_ids || []).includes(c.id))
                              .map((c) => <option key={c.id} value={c.id}>{`${c.handle} · ${c.network}${c.verified ? "" : " · à vérifier"}`}</option>)}
                          </optgroup>
                        ))}
                      </select>
                    </label>
                    <button onClick={() => depuisAnnuaire(l)} data-testid={`liste-ajouter-${l.id}`} className="bg-marine text-white rounded-sm px-5 py-2 text-sm hover:bg-marine-hover">Ajouter</button>
                  </div>

                  <div>
                    <div className="text-sm mb-1.5">Ou saisir un compte absent de l'annuaire <span className="text-xs text-marine/80">— il y entrera, marqué à vérifier</span></div>
                    <div className="grid sm:grid-cols-5 gap-2">
                      <input aria-label="Nom du compte" placeholder="Nom (facultatif)" value={neuf.name}
                        onChange={(e) => setNeuf({ ...neuf, name: e.target.value })} data-testid={`neuf-nom-${l.id}`} className="inp" />
                      <input aria-label="Identifiant du compte" placeholder="@compte ou lien" value={neuf.handle}
                        onChange={(e) => setNeuf({ ...neuf, handle: e.target.value })} data-testid={`neuf-handle-${l.id}`} className="inp" />
                      <select aria-label="Réseau" value={neuf.network} onChange={(e) => setNeuf({ ...neuf, network: e.target.value })}
                        data-testid={`neuf-reseau-${l.id}`} className="inp">
                        {Object.keys(POST_PLATFORMS).map((r) => <option key={r} value={r}>{POST_PLATFORMS[r].label}</option>)}
                      </select>
                      <select aria-label="Centre d'intérêt" value={neuf.entity_id} onChange={(e) => setNeuf({ ...neuf, entity_id: e.target.value })}
                        data-testid={`neuf-interet-${l.id}`} className="inp">
                        <option value="">— Nouveau centre d'intérêt —</option>
                        {annuaire.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                      </select>
                      <button onClick={() => ajouterNeuf(l)} data-testid={`neuf-ajouter-${l.id}`} className="bg-marine text-white rounded-sm text-sm px-4 hover:bg-marine-hover">Ajouter</button>
                    </div>
                    {!neuf.entity_id && (
                      <input aria-label="Nom du nouveau centre d'intérêt" placeholder="Nom du centre d'intérêt — ex. Nobel Biocare"
                        value={neuf.entity_name} onChange={(e) => setNeuf({ ...neuf, entity_name: e.target.value })}
                        data-testid={`neuf-interet-nom-${l.id}`} className="inp mt-2 max-w-md" />
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
